import React, { useRef } from "react";
import {
    motion,
    useScroll,
    useTransform,
    useMotionValue,
    useSpring,
} from "framer-motion";
import { TID } from "@/lib/testIds";
import { ArrowUpRight, Play } from "lucide-react";
import { useT } from "@/lib/i18n";

const LOGO_URL =
    "https://customer-assets-jt897jd0.emergentagent.net/job_417ef979-c5a1-480b-a52d-ba174268846c/artifacts/hbgmknso_669BBB83-039C-4BBD-AA28-5D92E2D6BCAC.png";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay = 0 }) => (
    <span className="mask-line">
        <motion.span
            initial={{ y: "110%", opacity: 0 }}
            animate={{ y: "0%", opacity: 1 }}
            transition={{ duration: 1.1, delay, ease: EASE }}
            style={{ display: "inline-block" }}
        >
            {children}
        </motion.span>
    </span>
);

export default function Hero() {
    const { t, lang } = useT();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
    const opacity = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 15 });
    const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 15 });
    const tx = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 80, damping: 20 });
    const ty = useSpring(useTransform(my, [-0.5, 0.5], [-14, 14]), { stiffness: 80, damping: 20 });

    const onMouseMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };
    const onMouseLeave = () => {
        mx.set(0);
        my.set(0);
    };

    return (
        <section
            id="top"
            ref={ref}
            data-testid={TID.hero.root}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
            className="relative bg-sn-black text-sn-silver min-h-[100svh] pt-32 md:pt-40 pb-24 overflow-hidden"
        >
            <div className="absolute inset-0 hero-radial" />
            <div className="absolute inset-0 hero-grid" />
            <div className="grain" />

            <div className="absolute top-24 left-6 md:left-10 font-mono-e text-[10px] tracking-[0.25em] text-sn-fog/60">
                41°54′10″N · 12°29′46″E
            </div>
            <div className="absolute top-24 right-6 md:right-10 font-mono-e text-[10px] tracking-[0.25em] text-sn-fog/60">
                SYS · SENTINEL v0.9
            </div>

            <motion.div
                key={lang}
                style={{ y, opacity }}
                className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 items-center min-h-[75svh]"
            >
                <div className="col-span-12 lg:col-span-7 pt-10 lg:pt-0">
                    <div className="overflow-hidden">
                        <motion.div
                            initial={{ y: 40, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.9, ease: EASE }}
                            className="flex items-center gap-3 mb-8"
                        >
                            <span className="inline-block w-6 h-px bg-sn-blue" />
                            <span
                                data-testid={TID.hero.eyebrow}
                                className="font-mono-e text-[11px] uppercase tracking-[0.28em] text-sn-fog"
                            >
                                {t("hero.eyebrow")}
                            </span>
                        </motion.div>
                    </div>

                    <h1
                        data-testid={TID.hero.headline}
                        className="font-serif text-[13vw] md:text-[10vw] lg:text-[8.2rem] xl:text-[9.5rem] leading-[0.92] tracking-[-0.03em]"
                    >
                        <MaskedLine delay={0.15}>{t("hero.headline_1")}</MaskedLine>
                        <MaskedLine delay={0.3}>
                            <span className="text-sn-fog">{t("hero.headline_2a")}</span>{" "}
                            <em className="italic text-sn-blue">{t("hero.headline_2b")}</em>
                        </MaskedLine>
                        <MaskedLine delay={0.45}>
                            <span className="text-sn-silver">{t("hero.headline_3a")}</span>{" "}
                            <span className="font-serif italic">{t("hero.headline_3b")}</span>
                        </MaskedLine>
                    </h1>

                    <motion.p
                        data-testid={TID.hero.subtitle}
                        initial={{ y: 30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
                        className="mt-10 max-w-xl text-base md:text-lg text-sn-fog leading-relaxed"
                    >
                        {t("hero.subtitle")}
                    </motion.p>

                    <motion.div
                        initial={{ y: 30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <a
                            href="#waitlist"
                            data-testid={TID.hero.ctaPrimary}
                            className="btn-sweep btn-sweep-dark inline-flex items-center gap-3 px-7 py-4 rounded-full bg-sn-blue text-white font-mono-e text-[11px] uppercase tracking-[0.24em] transition-colors duration-300"
                        >
                            {t("hero.ctaPrimary")}
                            <ArrowUpRight size={16} />
                        </a>
                        <a
                            href="#come-funziona"
                            data-testid={TID.hero.ctaSecondary}
                            className="inline-flex items-center gap-3 px-6 py-4 rounded-full border border-white/15 text-sn-silver font-mono-e text-[11px] uppercase tracking-[0.24em] hover:border-sn-blue hover:text-sn-blue transition-colors duration-300"
                        >
                            <Play size={14} className="fill-current" />
                            {t("hero.ctaSecondary")}
                        </a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1.2, delay: 1.1 }}
                        className="mt-16 flex items-center gap-6 font-mono-e text-[10px] tracking-[0.25em] text-sn-fog/70 uppercase"
                    >
                        <span className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-sn-blue rounded-full animate-pulse" />
                            {t("hero.liveNorm")}
                        </span>
                        <span className="hidden md:inline">·</span>
                        <span className="hidden md:inline">{t("hero.comingStores")}</span>
                    </motion.div>
                </div>

                <div className="col-span-12 lg:col-span-5 relative h-[420px] md:h-[560px] flex items-center justify-center">
                    <motion.div
                        style={{
                            rotateX: rx,
                            rotateY: ry,
                            x: tx,
                            y: ty,
                            transformStyle: "preserve-3d",
                            perspective: 1200,
                        }}
                        initial={{ scale: 0.85, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 1.4, delay: 0.4, ease: EASE }}
                        className="relative"
                        data-testid={TID.hero.shield}
                    >
                        <motion.div
                            initial={{ scale: 0.6, opacity: 0 }}
                            animate={{ scale: [0.6, 1.15, 0.6], opacity: [0, 0.35, 0] }}
                            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                            className="absolute inset-0 border border-sn-blue/40 rounded-full"
                        />
                        <motion.div
                            initial={{ scale: 0.4, opacity: 0 }}
                            animate={{ scale: [0.4, 1.4, 0.4], opacity: [0, 0.2, 0] }}
                            transition={{ duration: 4, repeat: Infinity, delay: 1.2, ease: "easeInOut" }}
                            className="absolute inset-0 border border-sn-blue/25 rounded-full"
                        />
                        <img
                            src={LOGO_URL}
                            alt="Sentinel Shield"
                            className="w-[340px] md:w-[460px] relative z-10 drop-shadow-[0_20px_60px_rgba(30,107,255,0.35)]"
                        />
                        <div className="absolute -top-4 left-1/2 -translate-x-1/2 font-mono-e text-[9px] tracking-[0.3em] text-sn-fog/70 uppercase">
                            {t("hero.target")}
                        </div>
                        <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 font-mono-e text-[9px] tracking-[0.3em] text-sn-fog/70 uppercase">
                            {t("hero.shieldId")}
                        </div>
                    </motion.div>
                </div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 1 }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
            >
                <span className="font-mono-e text-[9px] tracking-[0.3em] text-sn-fog uppercase">
                    {t("hero.scroll")}
                </span>
                <motion.div
                    animate={{ y: [0, 8, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="w-px h-8 bg-gradient-to-b from-sn-blue to-transparent"
                />
            </motion.div>
        </section>
    );
}
